/**
 * The org tree — `organization-build-book.md` §6.8, `org-08-organization-tree.png`.
 *
 * A company, its regions and districts, and the branches where work actually
 * happens. Any level may be skipped, so the tree is drawn from `parentId`
 * rather than from three nested loops: a branch straight under the company is
 * the ordinary shape of a one-branch company and has to look ordinary.
 *
 * Dragging a row onto another opens the Move dialog pre-filled. It never
 * moves silently — moving a branch changes who can see it, and that is not a
 * thing to discover by having done it.
 */
import { useMemo, useRef, useState } from 'react'
import type { OrgUnitKind, OrgUnitRow } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { KindTile } from '../../builder/kind-tile.js'
import { Tag } from '../primitives.js'
import { CheckRow } from '../form/index.js'
import { Popover } from '../overlays.js'
import { IconChevronDown, IconChevronRight, IconFilter, IconGrip, IconSearch } from '../../frame/icons.js'

/** What a drag carries. One key, so a stray drop cannot mean anything else. */
const DRAG_TYPE = 'application/x-rowted-org-unit'

export interface OrgTreeProps {
  units: OrgUnitRow[]
  selectedId: string | null
  onSelect: (id: string) => void
  /** A row was dropped on another: open Move, never move. */
  onDropOn?: (movedId: string, ontoId: string) => void
}

/** The three kinds the filter offers. The company is never one of them. */
const FILTERABLE: readonly OrgUnitKind[] = ['region', 'district', 'branch']

export function OrgTree({ units, selectedId, onSelect, onDropOn }: OrgTreeProps) {
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [over, setOver] = useState<string | null>(null)
  const [kinds, setKinds] = useState<Set<OrgUnitKind>>(new Set(FILTERABLE))
  const [filtering, setFiltering] = useState(false)
  const filterButton = useRef<HTMLButtonElement>(null)

  const byParent = useMemo(() => {
    const map = new Map<string | null, OrgUnitRow[]>()
    for (const unit of units) {
      const list = map.get(unit.parentId) ?? []
      list.push(unit)
      map.set(unit.parentId, list)
    }
    return map
  }, [units])

  /*
   * A search that hid a matching branch's ancestors would hide the branch:
   * the tree is drawn by walking down from the roots, so a row whose parent
   * was filtered out is never reached. Matching rows therefore keep their
   * whole line of ancestors.
   */
  const term = query.trim().toLowerCase()
  const shown = useMemo(() => {
    if (!term) return null
    const byId = new Map(units.map((unit) => [unit.id, unit]))
    const keep = new Set<string>()
    for (const unit of units) {
      if (!unit.name.toLowerCase().includes(term)
        && !unit.code.toLowerCase().includes(term)) continue
      keep.add(unit.id)
      let at = unit.parentId
      while (at && !keep.has(at)) { keep.add(at); at = byId.get(at)?.parentId ?? null }
    }
    return keep
  }, [units, term])

  /*
   * A kind that is filtered out does not take its branches with it: the row
   * disappears and its children move up to its indent. Turning off Region and
   * District is how you ask for "just the branches", and a list of branches
   * nested three deep under two rows that are not drawn would be an answer to
   * a different question.
   */
  const rows: { unit: OrgUnitRow; depth: number }[] = []
  const walk = (parentId: string | null, depth: number) => {
    for (const unit of byParent.get(parentId) ?? []) {
      if (shown && !shown.has(unit.id)) continue
      const drawn = unit.kind === 'company' || kinds.has(unit.kind)
      if (drawn) rows.push({ unit, depth })
      if (drawn && collapsed.has(unit.id)) continue
      walk(unit.id, drawn ? depth + 1 : depth)
    }
  }
  walk(null, 0)

  const toggle = (id: string) => setCollapsed((was) => {
    const next = new Set(was)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <div className={cn('flex w-[400px] shrink-0 flex-col self-start overflow-hidden max-lg:w-full',
      'rounded-record border border-line bg-surface')}>
      <div className="flex gap-2 p-3 pb-2">
        <label className="relative block min-w-0 flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint">
            <IconSearch size={14} />
          </span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find a branch, district or region"
            aria-label="Find a branch, district or region"
            className={cn('h-[34px] w-full rounded-field border border-line bg-surface pl-8 pr-2.5',
              'text-cell text-text placeholder:text-text-faint',
              'focus:border-accent-line focus:outline-none focus:ring-2 focus:ring-accent-ring')}
          />
        </label>

        <button
          ref={filterButton}
          type="button"
          aria-label="Which kinds to show"
          aria-expanded={filtering}
          onClick={() => setFiltering((was) => !was)}
          className={cn('flex h-[34px] w-[34px] shrink-0 items-center justify-center',
            'rounded-field border text-text-muted hover:bg-hover',
            kinds.size < FILTERABLE.length
              ? 'border-accent-line bg-accent-soft text-accent'
              : 'border-line bg-surface')}
        >
          <IconFilter size={15} />
        </button>

        <Popover
          open={filtering}
          onClose={() => setFiltering(false)}
          anchor={filterButton.current}
          align="right"
          width={216}
          label="Which kinds to show"
        >
          <div className="px-3 py-1.5">
            {FILTERABLE.map((kind) => (
              <CheckRow
                key={kind}
                label={kind[0]!.toUpperCase() + kind.slice(1)}
                checked={kinds.has(kind)}
                onChange={(on) => setKinds((was) => {
                  const next = new Set(was)
                  if (on) next.add(kind)
                  else next.delete(kind)
                  return next
                })}
              />
            ))}
          </div>
        </Popover>
      </div>

      <div className="flex flex-col pb-2">
        {rows.length === 0 && (
          <p className="m-0 px-4 py-3 text-body text-text-faint">
            {term ? `Nothing matches “${query}”.` : 'No places yet.'}
          </p>
        )}

        {rows.map(({ unit, depth }) => {
          const children = (byParent.get(unit.id) ?? []).length > 0
          const selected = unit.id === selectedId
          /*
           * A region or district with no working branch under it is a heading
           * over nothing: no crew leaves from it, no job is filed to it, and
           * every report it appears on is empty. It reads like a closed
           * branch because it is the same fact — a place that is not doing
           * anything — and saying so is how somebody notices they made a
           * region and never put a branch in it.
           */
          const hollow = unit.kind !== 'branch' && unit.counts.branches === 0
          return (
            <div
              key={unit.id}
              draggable={Boolean(onDropOn) && unit.kind !== 'company'}
              onDragStart={(event) => {
                event.dataTransfer.setData(DRAG_TYPE, unit.id)
                event.dataTransfer.effectAllowed = 'move'
              }}
              onDragOver={(event) => {
                if (!onDropOn || !event.dataTransfer.types.includes(DRAG_TYPE)) return
                event.preventDefault()
                setOver(unit.id)
              }}
              onDragLeave={() => setOver((was) => (was === unit.id ? null : was))}
              onDrop={(event) => {
                event.preventDefault()
                setOver(null)
                const moved = event.dataTransfer.getData(DRAG_TYPE)
                if (moved && moved !== unit.id) onDropOn?.(moved, unit.id)
              }}
              style={{ paddingLeft: 10 + depth * 22 }}
              className={cn('mx-2 flex h-[42px] items-center gap-[9px] rounded-[10px] pr-2.5',
                selected
                  ? 'border border-accent-line bg-accent-soft'
                  : 'border border-transparent hover:bg-hover',
                over === unit.id && !selected && 'border-dashed border-accent-line',
                (!unit.active || hollow) && 'opacity-55')}
            >
              <button
                type="button"
                aria-label={children ? `${collapsed.has(unit.id) ? 'Expand' : 'Collapse'} ${unit.name}` : undefined}
                onClick={() => children && toggle(unit.id)}
                className="flex h-[14px] w-[14px] shrink-0 items-center justify-center text-text-faint"
              >
                {children && (collapsed.has(unit.id)
                  ? <IconChevronRight size={14} />
                  : <IconChevronDown size={14} />)}
              </button>

              <KindTile kind={unit.kind} size={28} />

              <button
                type="button"
                onClick={() => onSelect(unit.id)}
                className="flex min-w-0 flex-1 flex-col items-start gap-px text-left"
              >
                <span className={cn('flex min-w-0 max-w-full items-center gap-1.5 truncate',
                  'text-[13.5px] text-text',
                  selected || unit.kind === 'company' ? 'font-bold' : 'font-semibold')}
                >
                  <span className="truncate">{unit.name}</span>
                  {!unit.active && <Tag>Closed</Tag>}
                  {unit.active && hollow && <Tag>No branches</Tag>}
                </span>
                <span className="truncate text-[11px] text-text-muted">
                  {hollow ? 'nothing under it yet' : meta(unit)}
                </span>
              </button>

              {onDropOn && unit.kind !== 'company' && (
                <span aria-hidden className="shrink-0 text-line-strong"><IconGrip size={14} /></span>
              )}
            </div>
          )
        })}
      </div>

      <div className={cn('flex items-center gap-3.5 border-t border-line-faint px-4 py-2.5',
        'text-[11.5px] text-text-muted')}>
        {(['region', 'district', 'branch'] as const).map((kind) => (
          <span key={kind} className="flex items-center gap-1.5">
            <KindTile kind={kind} size={18} />
            {kind[0]!.toUpperCase() + kind.slice(1)}
          </span>
        ))}
        {onDropOn && <span className="ml-auto">drag to move</span>}
      </div>
    </div>
  )
}

/** "2 yards · 6 crews · 14 people" — only the parts that are not zero. */
function meta(unit: OrgUnitRow): string {
  const { branches, yards, crews, people } = unit.counts
  const parts: string[] = []
  if (unit.kind !== 'branch' && branches > 0) {
    parts.push(`${branches} branch${branches === 1 ? '' : 'es'}`)
  }
  if (yards > 0) parts.push(`${yards} yard${yards === 1 ? '' : 's'}`)
  if (crews > 0) parts.push(`${crews} crew${crews === 1 ? '' : 's'}`)
  if (people > 0) parts.push(`${people} ${people === 1 ? 'person' : 'people'}`)
  return parts.join(' · ')
}
